import { create } from 'zustand'
import axios from 'axios'
import toast from 'react-hot-toast/headless'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInAnonymously,
  signOut,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth'
import { auth } from '../lib/firebase'
import { API_BASE_URL } from '@config';

function firebaseErrorMessage(error) {
  const map = {
    'auth/email-already-in-use': 'An account with that email already exists',
    'auth/invalid-email': 'Invalid email address',
    'auth/weak-password': 'Password is too weak',
    'auth/user-not-found': 'Invalid credentials',
    'auth/wrong-password': 'Invalid credentials',
    'auth/invalid-credential': 'Invalid credentials',
    'auth/too-many-requests': 'Too many attempts — try again later'
  }
  return map[error.code] || 'Something went wrong, please try again'
}

// Set while register()/loginAsGuest() are doing their own sync, so the
// onAuthStateChanged listener (which also fires for that same sign-in) skips
// its own sync instead of racing it with stale data — see syncProfile below.
let suppressListenerSync = false

// The ID token's `name` claim does not reliably reflect a just-set
// displayName, so username always comes from the SDK's live User object
// instead (updateProfile() keeps this current with no token-refresh lag).
async function syncProfile(firebaseUser) {
  const tokenResult = await firebaseUser.getIdTokenResult()
  const response = await axios.post(
    `${API_BASE_URL}/auth/sync`,
    firebaseUser.displayName ? { username: firebaseUser.displayName } : {},
    { headers: { Authorization: `Bearer ${tokenResult.token}` }, timeout: 10000 }
  )
  return { profile: response.data.user, isAdmin: tokenResult.claims.admin === true }
}

export const useAuthStore = create((set, get) => ({
  user: null,
  isAdmin: false,
  isLoading: true,

  // Returns the unsubscribe function — the caller (App.jsx) must return it
  // from its effect. Without that, React StrictMode's double-invoked effect
  // leaves two listeners subscribed, both firing (and racing) on one sign-in.
  initializeAuth: () => {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        set({ user: null, isAdmin: false, isLoading: false })
        return
      }

      if (suppressListenerSync) return

      try {
        const { profile, isAdmin } = await syncProfile(firebaseUser)
        set({ user: profile, isAdmin, isLoading: false })
      } catch (error) {
        const message = error.response?.data?.error || 'Sign-in failed'
        toast.error(message)
        await signOut(auth)
        set({ user: null, isAdmin: false, isLoading: false })
      }
    })
  },

  register: async ({ email, password, username }) => {
    set({ isLoading: true })
    suppressListenerSync = true
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password)
      await updateProfile(credential.user, { displayName: username })
      const { profile, isAdmin } = await syncProfile(credential.user)
      set({ user: profile, isAdmin, isLoading: false })
      toast.success('Registration successful!')
      return true
    } catch (error) {
      toast.error(firebaseErrorMessage(error))
      set({ isLoading: false })
      return false
    } finally {
      suppressListenerSync = false
    }
  },

  login: async ({ email, password }) => {
    set({ isLoading: true })
    try {
      await signInWithEmailAndPassword(auth, email, password)
      toast.success('Login successful!')
      return true
    } catch (error) {
      toast.error(firebaseErrorMessage(error))
      set({ isLoading: false })
      return false
    }
  },

  // Success toast is left to the caller (LoginPage) so it can use the
  // translated `resetEmailSent` copy — unlike the other actions here, whose
  // toasts are pre-existing hardcoded English. `auth/user-not-found` is
  // treated as success (same true return, no error toast) so this can never
  // be used to check whether an email is registered.
  resetPassword: async (email) => {
    try {
      // Shows a "Continue" link back to the app on Firebase's hosted reset
      // page once the user finishes. The target origin must be listed under
      // Firebase Console -> Authentication -> Settings -> Authorized domains
      // or this throws auth/unauthorized-continue-uri.
      await sendPasswordResetEmail(auth, email, { url: window.location.origin })
      return true
    } catch (error) {
      if (error.code === 'auth/user-not-found') return true
      toast.error(firebaseErrorMessage(error))
      return false
    }
  },

  loginAsGuest: async (username) => {
    set({ isLoading: true })
    suppressListenerSync = true
    try {
      const credential = await signInAnonymously(auth)
      await updateProfile(credential.user, { displayName: username })
      const { profile, isAdmin } = await syncProfile(credential.user)
      set({ user: profile, isAdmin, isLoading: false })
      toast.success(`Welcome, ${username}!`)
      return true
    } catch (error) {
      toast.error('Guest login failed')
      set({ isLoading: false })
      return false
    } finally {
      suppressListenerSync = false
    }
  },

  logout: async () => {
    try {
      const idToken = await auth.currentUser?.getIdToken()
      if (idToken) {
        await axios
          .post(`${API_BASE_URL}/auth/logout`, {}, { headers: { Authorization: `Bearer ${idToken}` } })
          .catch(() => {})
      }
    } finally {
      await signOut(auth)
      toast.success('Logged out successfully')
    }
  },

  updateUser: (userData) => {
    set({ user: { ...get().user, ...userData } })
  }
}))
